<template>
  <div class="relative z-50">
    <!-- Sliding panel -->
    <transition name="slide-up">
      <div
        v-if="activePanel"
        class="bg-white shadow-mobile rounded-t-[32px] z-30 fixed bottom-[100px] left-0 w-screen px-[12px] py-[16px]"
      >
        <div class="bg-white p-4 w-full flex justify-center">
          <div v-if="activePanel === 'shapes'" class="shapes-panel">
            <div class="flex flex-wrap gap-y-3 my-5">
              <button
                class="select-shape-button basis-1/3"
                :class="{ active: isRectangle }"
                @click="designerStore.setShape(DieCutMaskShape.Rectangle)"
              >
                <svg
                  width="28"
                  height="27"
                  viewBox="0 0 28 27"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect x="0.935547" width="26.5169" height="26.5169" fill="#7F75B2" />
                </svg>
                <span>Square</span>
              </button>

              <button
                class="select-shape-button basis-1/3"
                :class="{ active: isTriangle }"
                @click="designerStore.setShape(DieCutMaskShape.Triangle)"
              >
                <svg
                  width="32"
                  height="28"
                  viewBox="0 0 32 28"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15.8076 0.663086L31.1171 27.18H0.498047L15.8076 0.663086Z"
                    fill="#7F75B2"
                  />
                </svg>
                <span>Triangle</span>
              </button>
              <button
                class="select-shape-button basis-1/3"
                :class="{ active: isCircle }"
                @click="designerStore.setShape(DieCutMaskShape.Circle)"
              >
                <svg
                  width="28"
                  height="27"
                  viewBox="0 0 28 27"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect x="0.548828" width="26.5169" height="26.5169" rx="13.2585" fill="#FFCF57" />
                </svg>
                <span>Circle</span>
              </button>
            </div>
          </div>
          <div v-else-if="activePanel === 'width'">
            <ui-input
              type="number"
              max="400"
              v-model="designerStore.dieCutMask.width"
              append-text="mm"
              title="Width"
            />
          </div>
          <div v-else-if="activePanel === 'height'">
            <ui-input
              type="number"
              max="400"
              v-model="designerStore.dieCutMask.height"
              append-text="mm"
              title="Height"
            />
          </div>
          <div v-else-if="activePanel === 'radius'" class="flex flex-row gap-4">
            <ui-input
              type="number"
              v-model="designerStore.dieCutMask.radiusX"
              append-text="mm"
              title="Radius X"
              class="flex-1"
            />
            <ui-input
              type="number"
              v-model="designerStore.dieCutMask.radiusY"
              append-text="mm"
              title="Radius Y"
              class="flex-1"
            />
          </div>
        </div>

        <!-- Texts panel -->
        <div v-if="activePanel === 'texts'" class="texts-panel w-full max-h-[60vh] overflow-y-auto px-2">
          <div v-if="designerStore.textLayers.length === 0" class="text-center text-gray-400 text-sm py-6">
            No text layers yet
          </div>

          <div
            v-for="layer in designerStore.textLayers"
            :key="layer.id"
            class="text-layer-card mb-3 border border-gray-200 rounded-2xl overflow-hidden bg-white"
          >
            <!-- Card header -->
            <div
              class="flex items-center justify-between px-4 py-3 cursor-pointer"
              @click="toggleLayer(layer.id)"
            >
              <div class="flex items-center gap-2 flex-1 min-w-0">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 7V4H20V7M9 20H15M12 4V20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span class="text-sm truncate">{{ layer.text || 'Empty text' }}</span>
              </div>
              <svg
                :class="['transition-transform duration-200 shrink-0', expandedLayerId === layer.id ? 'rotate-180' : '']"
                width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>

            <!-- Expanded edit controls -->
            <div v-if="expandedLayerId === layer.id" class="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
              <!-- Text content -->
              <ui-input
                :model-value="layer.text"
                @update:model-value="layer.text = $event as string"
                placeholder="Enter text"
                textarea
                :input-class="'!min-h-[60px] !rounded-xl !text-sm !px-3 !py-2'"
              />

              <!-- Font + Size -->
              <div class="flex gap-2">
                <select
                  :value="layer.font"
                  @change="onMobileFontChange(layer, ($event.target as HTMLSelectElement).value)"
                  class="mobile-select flex-1"
                >
                  <option v-for="font in Object.keys(fontOptions)" :key="font" :value="font">{{ font }}</option>
                </select>
                <select
                  :value="layer.fontSize"
                  @change="layer.fontSize = Number(($event.target as HTMLSelectElement).value)"
                  class="mobile-select w-20"
                >
                  <option v-for="size in fontSizes" :key="size" :value="size">{{ size }}pt</option>
                </select>
              </div>

              <!-- Weight + Color + Alignment -->
              <div class="flex items-center gap-2 flex-wrap">
                <select
                  :value="layer.fontWeight"
                  @change="layer.fontWeight = ($event.target as HTMLSelectElement).value"
                  class="mobile-select"
                >
                  <option v-for="w in (fontOptions[layer.font] || ['400'])" :key="w" :value="w">{{ w }}</option>
                </select>

                <input
                  type="color"
                  :value="layer.color"
                  @input="layer.color = ($event.target as HTMLInputElement).value"
                  class="h-9 w-9 rounded-lg border border-gray-200 p-0.5 cursor-pointer"
                  style="background: none"
                />

                <div class="inline-flex alignment-group-mobile">
                  <button
                    v-for="option in alignOptions"
                    :key="option.value"
                    class="align-btn-mobile"
                    :class="{ active: layer.align === option.value }"
                    @click="layer.align = option.value as 'left' | 'center' | 'right' | 'justify'"
                  >
                    <span v-html="option.icon" />
                  </button>
                </div>
              </div>

              <!-- Delete -->
              <button
                class="flex items-center gap-2 text-sm text-red-400 pt-1"
                @click="designerStore.deleteTextLayer(layer.id)"
              >
                <IconDelete />
                <span>Delete layer</span>
              </button>
            </div>
          </div>

          <!-- Add text layer button -->
          <button
            class="w-full py-3 text-sm font-bold text-black border border-dashed border-gray-300 rounded-2xl mt-1 mb-2"
            @click="onAddTextLayer"
          >
            + Add text
          </button>
        </div>
      </div>
    </transition>

    <!-- Bottom fixed bar -->
    <div
      class="fixed bottom-0 left-0 w-full h-[115px] bg-white z-50 px-2 sm:px-8 pt-[25px] pb-[20px] flex justify-center items-center"
      :class="{ 'shadow-mobile': !activePanel }"
    >
      <button
        class="text-black flex flex-col items-center font-bold text-sm p-[11px]"
        @click="togglePanel('shapes')"
      >
        <svg
          width="25"
          height="25"
          viewBox="0 0 25 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.5 8.1352C3.5 6.45505 3.5 5.61497 3.82698 4.97323C4.1146 4.40875 4.57354 3.94981 5.13803 3.66219C5.77976 3.33521 6.61984 3.33521 8.3 3.33521H16.7C18.3802 3.33521 19.2202 3.33521 19.862 3.66219C20.4265 3.94981 20.8854 4.40875 21.173 4.97323C21.5 5.61497 21.5 6.45505 21.5 8.13521V16.5352C21.5 18.2154 21.5 19.0554 21.173 19.6972C20.8854 20.2617 20.4265 20.7206 19.862 21.0082C19.2202 21.3352 18.3802 21.3352 16.7 21.3352H8.3C6.61984 21.3352 5.77976 21.3352 5.13803 21.0082C4.57354 20.7206 4.1146 20.2617 3.82698 19.6972C3.5 19.0554 3.5 18.2154 3.5 16.5352V8.1352Z"
            stroke="black"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

        <span>Shapes</span>
      </button>
      <button
        class="text-black flex flex-col items-center font-bold text-sm p-[11px]"
        @click="togglePanel('width')"
      >
        <svg
          width="25"
          height="25"
          viewBox="0 0 25 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2.92409 12.3352H22.0759M2.92409 12.3352L6.11606 15.5272M2.92409 12.3352L6.11606 9.14322M22.0759 12.3352L18.8839 9.14322M22.0759 12.3352L18.8839 15.5272"
            stroke="black"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

        <span>Width</span>
      </button>
      <button
        class="text-black flex flex-col items-center font-bold text-sm p-[11px]"
        @click="togglePanel('height')"
      >
        <svg
          width="25"
          height="25"
          viewBox="0 0 25 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.5003 2.75929V21.9111M12.5003 2.75929L9.30829 5.95126M12.5003 2.75929L15.6922 5.95126M12.5003 21.9111L15.6922 18.7191M12.5003 21.9111L9.30829 18.7191"
            stroke="black"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

        <span>Height</span>
      </button>
      <button
        class="text-black flex flex-col items-center font-bold text-sm p-[11px]"
        @click="togglePanel('radius')"
      >
        <svg
          width="25"
          height="25"
          viewBox="0 0 25 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.5 3.33521H8.3C6.61984 3.33521 5.77976 3.33521 5.13803 3.66219C4.57354 3.94981 4.1146 4.40875 3.82698 4.97323C3.5 5.61497 3.5 6.45505 3.5 8.13521V8.33521M8.5 21.3352H8.3C6.61984 21.3352 5.77976 21.3352 5.13803 21.0082C4.57354 20.7206 4.1146 20.2617 3.82698 19.6972C3.5 19.0554 3.5 18.2154 3.5 16.5352V16.3352M21.5 8.33521V8.1352C21.5 6.45505 21.5 5.61497 21.173 4.97323C20.8854 4.40875 20.4265 3.94981 19.862 3.66219C19.2202 3.33521 18.3802 3.33521 16.7 3.33521H16.5M21.5 16.3352V16.5352C21.5 18.2154 21.5 19.0554 21.173 19.6972C20.8854 20.2617 20.4265 20.7206 19.862 21.0082C19.2202 21.3352 18.3802 21.3352 16.7 21.3352H16.5"
            stroke="black"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>

        <span>Radius</span>
      </button>

      <!-- Texts button -->
      <button
        class="text-black flex flex-col items-center font-bold text-sm p-[11px]"
        @click="togglePanel('texts')"
      >
        <svg
          width="25"
          height="25"
          viewBox="0 0 25 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4.5 7.33521V4.33521H20.5V7.33521M9.5 20.3352H15.5M12.5 4.33521V20.3352"
            stroke="black"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span>Texts</span>
      </button>

      <button v-if="!isAdmin" class="check-button shrink-0" @click="onContinue">
        <svg
          width="25"
          height="25"
          viewBox="0 0 25 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20.5 6.33521L9.5 17.3352L4.5 12.3352"
            stroke="white"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <button v-else class="check-button" @click="togglePanel(null)">
        <svg
          width="25"
          height="25"
          viewBox="0 0 25 25"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M20.5 6.33521L9.5 17.3352L4.5 12.3352"
            stroke="white"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import UiInput from '@/components/ui/UiInput.vue'
import IconDelete from '@/components/ui/IconDelete.vue'
import { computed, ref } from 'vue'
import { DieCutMaskShape, useDesignerStore } from '@/stores/designerStore'
import { useAdminDesignerStore } from '@/stores/adminDesignerStore'
import WebFont from 'webfontloader'

const designerStore = useDesignerStore()
const activePanel = ref<string | null>(null)
const expandedLayerId = ref<string | null>(null)
const isAdmin = computed(() => useAdminDesignerStore().embedded === true)

const emit = defineEmits(['continueClicked'])

function togglePanel(panel: string | null) {
  activePanel.value = activePanel.value === panel ? null : panel
}

function toggleLayer(id: string) {
  expandedLayerId.value = expandedLayerId.value === id ? null : id
}

function onContinue() {
  emit('continueClicked')
}

function onAddTextLayer() {
  designerStore.addTextLayer()
  const newLayer = designerStore.textLayers[designerStore.textLayers.length - 1]
  if (newLayer) {
    expandedLayerId.value = newLayer.id
  }
}

// --- Text editing data (same as WidgetTextLayerControl) ---

const fontOptions: Record<string, string[]> = {
  Arial: ['400', '700'],
  Roboto: ['100', '300', '400', '500', '700', '900'],
  'Open Sans': ['300', '400', '600', '700', '800'],
}

const systemFonts = ['Arial']

const fontSizes = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 64, 72]

function loadGoogleFont(font: string) {
  if (systemFonts.includes(font)) return
  const weights = fontOptions[font]?.join(',') || '400'
  WebFont.load({
    google: {
      families: [`${font}:${weights}`],
    },
  })
}

function onMobileFontChange(layer: typeof designerStore.textLayers[0], font: string) {
  layer.font = font
  loadGoogleFont(font)
  const weights = fontOptions[font]
  if (weights && !weights.includes(layer.fontWeight)) {
    layer.fontWeight = weights.includes('400') ? '400' : weights[0]
  }
}

const alignOptions = [
  {
    value: 'left',
    icon: `<svg width="18" height="18" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.5 5.5H2M10.5 3.5H2M10.5 7.5H2M8.5 9.5H2" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
  },
  {
    value: 'center',
    icon: `<svg width="18" height="18" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.5 5.5H3.5M11 3.5H2M11 7.5H2M9.5 9.5H3.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
  },
  {
    value: 'right',
    icon: `<svg width="18" height="18" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 5.5H4.5M11 3.5H2.5M11 7.5H2.5M11 9.5H4.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
  },
  {
    value: 'justify',
    icon: `<svg width="18" height="18" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 5.5H2M11 9.5H2M11 3.5H2M11 7.5H2" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`,
  },
]

const isRectangle = computed(() => designerStore.dieCutMask.type === 'rectangle')
const isTriangle = computed(() => designerStore.dieCutMask.type === 'triangle')
const isCircle = computed(() => designerStore.dieCutMask.type === 'circle')
</script>

<style scoped>
.shadow-mobile {
  box-shadow:
    0 -24px 10px rgba(135, 108, 49, 0.01),
    0 -13px 8px rgba(135, 108, 49, 0.04),
    0 -6px 6px rgba(135, 108, 49, 0.07),
    0 -1px 3px rgba(135, 108, 49, 0.08);
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.shapes-panel {
  padding: 16px;
  position: relative;
  z-index: 3;
  height: 147px;
  width: 100%;
  overflow-y: scroll;

  &:before {
    z-index: -1;
    content: '';
    box-sizing: border-box;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
    background: #ffffff;
    border: 1px dashed rgba(0, 0, 0, 0.2);
    border-radius: 28px;
  }
}

.check-button {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border: 3px solid #fff;
  width: 50px;
  height: 50px;
  border-radius: 50%;

  /* White */
  background: #000;
  /* Shadow/Yellow/Small */
  box-shadow:
    5px 2px 2px rgba(98, 76, 29, 0.04),
    3px 1px 2px rgba(98, 76, 29, 0.12),
    1px 0px 1px rgba(98, 76, 29, 0.2),
    0px 0px 1px rgba(98, 76, 29, 0.24);
}

@media (min-width: 640px) {
  .check-button {
    border-width: 4px;
    width: 65px;
    height: 65px;
  }
}

/* Text editing mobile styles */
.mobile-select {
  border: 1px solid #e5e5e5;
  padding: 6px 8px;
  border-radius: 8px;
  font-size: 13px;
  background: #fff;
  appearance: auto;
}

.alignment-group-mobile {
  display: inline-flex;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
}

.align-btn-mobile {
  all: unset;
  padding: 6px 7px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-left: 1px solid #e5e5e5;

  &:first-child {
    border-left: none;
  }

  svg {
    display: block;
  }

  &.active {
    background-color: #f0f0f0;
  }
}

.text-layer-card {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}
</style>
